import React from "react";
import { useTranslationHook } from "../../hooks";
import {
  Box,
  Button,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

import { enqueueSnackbar } from "notistack";
import {
  getProcedureStages,
  procedureNextStage,
  startProcedure,
  closeProcedure,
  cancelProcedure,
  listTenders,
  getProcedure,
} from "../../api/procedure";
import { Stage } from "../../types/model/stage";
import { Procedure, ProcedureRole } from "../../types/model/procedure";
import { SendEmailRequest } from "../../types/model/email/SendEmailRequest";
import { sendEmail } from "../../api/email/sendEmail";
import { getProposal } from "../../api/stage/proposals/getProposal";
import { getAccountEmails } from "../../api/account";
import RecipientSelectionDialog from "../Generic/RecipientSelectionDialog";
import { useDispatch } from "react-redux";
import { setLoading } from "../../store/UI";

interface TenderAccountEmails {
  accountId: number;
  accountName: string;
  emails: string[];
}

interface StageListProps {
  projectId?: number;
  activityId?: number;
  procedureId?: number;
  procedure: Procedure;
  procedureRole: ProcedureRole;
  onStageSelected?: (projectId: number) => void;
  onProcedureUpdate?: () => void;
}

const StageList = (props: StageListProps) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const [stageList, setStageList] = React.useState<Stage[]>([]);
  const [selectedCard, setSelectedCard] = React.useState(0);
  const [isInputDialogOpen, setIsInputDialogOpen] = React.useState(false);
  const [selectedRecipientEmails, setSelectedRecipientEmails] = React.useState<
    string[]
  >([]);
  const [tenderAccountEmails, setTenderAccountEmails] = React.useState<
    TenderAccountEmails[]
  >([]);

  const {
    projectId,
    activityId,
    procedureId,
    procedure,
    procedureRole,
    onStageSelected,
    onProcedureUpdate,
  } = props;

  const getStageList = () => {
    getProcedureStages(projectId!, activityId!, procedureId!)
      .then((response) => {
        setStageList(response);
        enqueueSnackbar(t("stages.stage-list-load-success"), {
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar(t("stages.stage-list-load-error"), {
          variant: "error",
        });
      })
      .finally(() => {});
  };

  const handleProcedureStart = async () => {
    try {
      await startProcedure(projectId!, activityId!, procedureId!);
      const tenders = await listTenders(projectId!, activityId!, procedureId!);
      const tenderEmailsData: TenderAccountEmails[] = await Promise.all(
        tenders.map(async (tender) => {
          const emails = await getAccountEmails(tender.id);
          return {
            accountId: tender.id,
            accountName: tender.name,
            emails: emails,
          };
        }),
      );
      setTenderAccountEmails(tenderEmailsData);
      setIsInputDialogOpen(true);
    } catch (error) {}
  };

  const sendNotifications = async (selectedRecipients: string[]) => {
    const latestProcedure = await getProcedure(
      projectId!,
      activityId!,
      procedureId!,
    );

    const proposalStage = stageList.find((s) =>
      s.stageTypeLabel?.toLowerCase().includes("proposal"),
    );
    let endDateStr: string | undefined;
    if (proposalStage) {
      const proposal = await getProposal(
        projectId!,
        activityId!,
        procedureId!,
        proposalStage.id,
      );
      if (proposal.endDate) {
        endDateStr = new Intl.DateTimeFormat("pt-PT").format(
          new Date(proposal.endDate),
        );
      }
    }

    for (const tenderAccount of tenderAccountEmails) {
      const recipientsForThisAccount = tenderAccount.emails.filter((email) =>
        selectedRecipients.includes(email),
      );

      if (recipientsForThisAccount.length > 0) {
        try {
          const emailVariables: any = {
            procedureName: latestProcedure.name,
            accountName: tenderAccount.accountName,
          };
          if (endDateStr) {
            emailVariables.endDate = endDateStr;
          }
          if (latestProcedure.contactManagerId) {
            emailVariables.contactManagerId = latestProcedure.contactManagerId;
          }

          const emailData: SendEmailRequest = {
            to: recipientsForThisAccount,
            cc: [],
            template: "convite_procedimento",
            variables: emailVariables,
          };
          await sendEmail(emailData);
          enqueueSnackbar(t("tender.add-success"), { variant: "success" });
        } catch (error) {
          enqueueSnackbar(t("error.tender-add-error"), { variant: "error" });
        }
      }
    }
  };

  const handleProcedureNextStage = () => {
    dispatch(setLoading(true));
    try {
      procedureNextStage(projectId!, activityId!, procedureId!)
        .then((response) => {
          onProcedureUpdate?.();
        })
        .catch((error) => {})
        .finally(() => {
          dispatch(setLoading(false));
        });
    } catch (error) {
      dispatch(setLoading(false));
    }
  };

  const handleProcedureCancel = () => {
    cancelProcedure(projectId!, activityId!, procedureId!)
      .then((response) => {
        onProcedureUpdate?.();
      })
      .catch((error) => {});
  };

  const handleProcedureClose = () => {
    closeProcedure(projectId!, activityId!, procedureId!)
      .then((response) => {
        onProcedureUpdate?.();
      })
      .catch((error) => {});
  };

  const handleStageClick = (stageId: number) => {
    onStageSelected?.(stageId);
  };

  React.useEffect(() => {
    if (selectedCard === 0) return;
    onStageSelected?.(selectedCard);
  }, [selectedCard]);

  React.useEffect(() => {
    getStageList();
  }, []);

  return (
    <>
      <Box sx={{ width: "100%", paddingTop: "1rem", justifyItems: "center" }}>
        <Typography color="primary" variant="h3">
          {t("stages.stages-list")}
        </Typography>
      </Box>
      <Box sx={{ width: "100%", paddingTop: "2rem" }}>
        {stageList && stageList.length > 0 && (
          <Stepper alternativeLabel nonLinear activeStep={-1}>
            {stageList.map((stage) => {
              const isReportStage =
                stage.stageTypeLabel === "generic.stages.PRELIMINARY-REPORT" ||
                stage.stageTypeLabel === "generic.stages.AWARD-REPORT" ||
                stage.stageTypeLabel === "generic.stages.REPORT";
              const isDisabled =
                (!procedureRole.isManager &&
                  !procedureRole.isOwner &&
                  !(
                    procedureRole.isTender &&
                    (stage.opened || stage.closed)
                  )) ||
                (procedureRole.isTender && isReportStage);
              return (
                <Step
                  key={"step-" + stage.id}
                  completed={stage.closed}
                  active={stage.opened}
                >
                  {isDisabled ? (
                    <StepLabel>{t(stage.stageTypeLabel)}</StepLabel>
                  ) : (
                    <StepButton
                      color="inherit"
                      onClick={() => handleStageClick(stage.id)}
                    >
                      <StepLabel>{t(stage.stageTypeLabel)}</StepLabel>
                    </StepButton>
                  )}
                </Step>
              );
            })}
          </Stepper>
        )}
      </Box>
      {procedureRole.isManager && (
        <>
          <Box
            sx={{ width: "100%", paddingTop: "2rem", justifyItems: "center" }}
          >
            <Typography color="primary" variant="h3">
              {t("procedures.control")}
            </Typography>
          </Box>
          <Box
            sx={{ width: "100%", paddingTop: "1rem", justifyItems: "center" }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                disabled={
                  procedure.started || procedure.canceled || procedure.closed
                }
                onClick={() => {
                  handleProcedureStart();
                }}
              >
                {t("procedures.start-procedure")}
              </Button>
              <Button
                variant="outlined"
                disabled={
                  !procedure.started ||
                  procedure.canceled ||
                  procedure.lastStage === procedure.stage ||
                  procedure.closed
                }
                onClick={(event) => {
                  handleProcedureNextStage();
                }}
              >
                {t("procedures.next-stage-procedure")}
              </Button>
              <Button
                variant="outlined"
                disabled={
                  !procedure.started || procedure.canceled || procedure.closed
                }
                onClick={() => {
                  handleProcedureCancel();
                }}
              >
                {t("procedures.cancel-procedure")}
              </Button>
              <Button
                variant="outlined"
                disabled={
                  !procedure.started ||
                  procedure.canceled ||
                  procedure.lastStage !== procedure.stage ||
                  procedure.closed
                }
                onClick={() => {
                  handleProcedureClose();
                }}
              >
                {t("procedures.close-procedure")}
              </Button>
            </Stack>
          </Box>
        </>
      )}
      <RecipientSelectionDialog
        open={isInputDialogOpen}
        title={t("tender.send-notification-title")}
        tenderAccountEmails={tenderAccountEmails}
        selectedEmails={selectedRecipientEmails}
        onSelectionChange={(emails) => setSelectedRecipientEmails(emails)}
        onConfirm={async () => {
          await sendNotifications(selectedRecipientEmails);
          setIsInputDialogOpen(false);
          onProcedureUpdate?.();
        }}
        onCancel={() => {
          setIsInputDialogOpen(false);
          onProcedureUpdate?.();
        }}
      />
    </>
  );
};

export default StageList;
